import { useEffect, useMemo, useRef } from 'react'
import { Dispatch } from 'redux'

export interface CountdownConfig {
  completed?: number
  error?: number
}

export const useCountdownTimeout = (
  status: string,
  countdownConfig: CountdownConfig = {},
  dispatch: Dispatch,
  messageIndex: number
) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastStatusRef = useRef<string | null>(null)

  // Memoize config values to avoid unnecessary effect re-runs when object reference changes
  const completedDuration = countdownConfig?.completed
  const errorDuration = countdownConfig?.error
  const configValues = useMemo(() => ({ completed: completedDuration, error: errorDuration }), [
    completedDuration,
    errorDuration,
  ])

  useEffect(() => {
    const countdownDuration: Record<string, number> = {}
    if (configValues.completed !== undefined) countdownDuration.completed = configValues.completed
    if (configValues.error !== undefined) countdownDuration.error = configValues.error

    const duration = countdownDuration[status]
    const statusChanged = lastStatusRef.current !== status

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    if (duration && statusChanged) {
      lastStatusRef.current = status
      // Set timeout to clear state when animation completes
      timeoutRef.current = setTimeout(() => {
        dispatch({
          type: 'UPDATE_TOOL_EXECUTION_STATE',
          messageIndex,
          toolExecutionState: undefined as any,
        })
      }, duration * 1000) // Convert seconds to milliseconds
    } else if (statusChanged) {
      lastStatusRef.current = status
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [status, configValues, dispatch, messageIndex])
}
